import moment from 'moment';
import { omitBy, uniq } from 'lodash-es';
import type { ConnectedTypes } from '../make-connected';
import { idOf } from './input/util/util';
import { getDayIndex, getVertexId } from '../../core/util';
import type { DateType } from '../../common/types';
import type { Types } from '../types';
import { makeChainable } from '../../common/make-chainable';

function getPosition (
  start: DateType,
  end:   DateType
): Pick<Types.eventConfiguration, 'day' | 'start' | 'end'> {
  const mmtMidnight = moment.utc(start).clone().startOf('day');
  return {
    day:   getDayIndex(start),
    start: moment.utc(start).diff(mmtMidnight, 'minutes'),
    end:   moment.utc(end  ).diff(mmtMidnight, 'minutes'),
  };
}

export function initialConfiguration (
  events:      ConnectedTypes.event[],
  lockedTimes: ConnectedTypes.lockedTime[],
  options:     Types.parsedToOptions
): Types.configuration[] {
  const fromEvents = events
    .map(x => {
      if (x.parked) return;
      if (!x.start || !x.end) return;

      const doc: Types.eventConfiguration = {
        id: idOf.event(x, options),
        ...getPosition(x.start, x.end),

        collection:   x.course ? getVertexId(x.course, options) : undefined,
        dependencies: x.inLocations?.map(x => x ? getVertexId(x, options) : null).filter(x => x != null),
      };
      return omitBy(doc, x => x == null) as Types.eventConfiguration;
    })
    .filter(x => x != null);

  const fromLockedTimes = lockedTimes
    .map(x => {
      if (!x.type || x.type == 'ANY') return;
      if (!x.start || !x.end) return;

      const doc: Types.eventConfiguration = {
        id: idOf.lockedTime(x, options),
        ...getPosition(x.start, x.end),

        dependencies: makeChainable(x.coalesced ?? [])
          .chain(
            x => x
              .filter(x => x.toModel == 'locations')
              .map(x => getVertexId(x.to, options)),
            x => uniq(x)
          )
          .value
      };
      return omitBy(doc, x => x == null) as Types.eventConfiguration;
    })
    .filter(x => x != null);

  return [{
    events: fromEvents.concat(fromLockedTimes)
  }];
};